import { useEffect, useRef } from 'react';

/**
 * Perundingan tombol panah dengan pembungkus Android TV.
 *
 * APK Lensa menyuntikkan navigasi spasial (`tv_dpad.js`) karena Chromium tidak
 * menyalakannya di WebView: tanpa itu remote tidak punya cara memindahkan fokus
 * sama sekali. Tapi beberapa layar di sini **sudah** punya papan ketiknya
 * sendiri (§G8.2, §G10.1), dan dua penggerak untuk satu tombol berarti satu
 * tekanan dieksekusi dua kali.
 *
 * Perundingannya **sinkron dan mendahului**: shim memanggil predikat ini
 * sebelum event merambat, lalu diam kalau jawabannya `true`. Bentuk itu bukan
 * selera. Versi sebelumnya menebak dari `defaultPrevented` sesudah event lewat,
 * dan konsekuensinya shim tidak bisa lagi memanggil `preventDefault()` — aksi
 * bawaan browser terlanjur jalan. Di sebagian box TV, aksi bawaan itu adalah
 * spatial navigation milik WebView sendiri: fokus berpindah sekali oleh
 * browser, sekali lagi oleh shim, dan terlihat sebagai lompatan dua langkah
 * yang tidak bisa dijelaskan dari sisi halaman.
 *
 * Predikatnya dipakai **juga** oleh penangan `keydown` yang bersangkutan, bukan
 * ditulis dua kali: dua salinan aturan yang sama pasti akan berbeda suatu hari,
 * dan bedanya akan muncul sebagai tombol yang dieksekusi dobel lagi.
 */
export type ArahPanah = 'up' | 'down' | 'left' | 'right';
export type KlaimPanah = (arah: ArahPanah) => boolean;

/**
 * Ditumpuk: viewer satu kamera dirender **di dalam** dashboard, jadi ada saat
 * keduanya hidup bersamaan. Yang terakhir dipasang yang menjawab, dan saat ia
 * lepas jawabannya kembali ke pemegang di bawahnya — bukan hilang.
 */
const pemegang: { jawab: KlaimPanah }[] = [];

function terapkan(): void {
  const atas = pemegang[pemegang.length - 1];
  (window as unknown as { __lensaPanah?: KlaimPanah }).__lensaPanah = atas?.jawab;
}

export function usePanahTv(klaim: KlaimPanah | null): void {
  /*
   * Predikatnya dibaca lewat ref, dan efeknya dipasang sekali saja.
   * Kalau efek ini bergantung pada `klaim`, ia akan melepas-pasang dirinya tiap
   * render — dan urutan tumpukannya ikut berubah, jadi dashboard bisa naik ke
   * atas viewer yang sedang terbuka hanya karena ada state lain yang berubah.
   */
  const ref = useRef(klaim);
  ref.current = klaim;

  useEffect(() => {
    const entri = { jawab: (arah: ArahPanah) => !!ref.current?.(arah) };
    pemegang.push(entri);
    terapkan();
    return () => {
      const i = pemegang.indexOf(entri);
      if (i >= 0) pemegang.splice(i, 1);
      terapkan();
    };
  }, []);
}
